import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { LuCalendar, LuChevronLeft, LuChevronRight } from "react-icons/lu";
import "./ModernDateTimePicker.css";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export default function ModernDatePicker({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = "YYYY-MM-DD",
  disabled = false,
  onDisabledClick,
  error = false,
  className = "",
  label,
  id,
  format = "YYYY-MM-DD",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Normalize current selected value as Moment
  const selectedMoment = value && moment(value).isValid() ? moment(value) : null;

  // View month state (what month the calendar grid is displaying)
  const [viewDate, setViewDate] = useState(() => {
    return selectedMoment ? selectedMoment.clone().startOf("month") : moment().startOf("month");
  });

  // Temporary selected date before clicking Apply
  const [pendingDate, setPendingDate] = useState(selectedMoment);

  // Sync viewDate and pendingDate when value changes or modal opens
  useEffect(() => {
    if (selectedMoment) {
      setPendingDate(selectedMoment);
      setViewDate(selectedMoment.clone().startOf("month"));
    } else {
      setPendingDate(null);
      const initialMonth = minDate && moment(minDate).isValid() ? moment(minDate).clone().startOf("month") : moment().startOf("month");
      setViewDate(initialMonth);
    }
  }, [value, isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleTriggerClick = (e) => {
    e.preventDefault();
    if (disabled) {
      if (onDisabledClick) onDisabledClick();
      return;
    }
    setIsOpen((prev) => !prev);
  };

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    setViewDate((prev) => prev.clone().subtract(1, "month"));
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    setViewDate((prev) => prev.clone().add(1, "month"));
  };

  const handleSelectDay = (dayMoment) => {
    setPendingDate(dayMoment);
  };

  const handleApply = (e) => {
    e.stopPropagation();
    if (pendingDate && onChange) {
      onChange(pendingDate);
    }
    setIsOpen(false);
  };

  const handleCancel = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setPendingDate(selectedMoment);
    setIsOpen(false);
  };

  // Build 7-column calendar matrix for viewDate month
  const startOfMonth = viewDate.clone().startOf("month");
  const daysInMonth = viewDate.daysInMonth();
  const startDayOfWeek = startOfMonth.day(); // 0 = Sunday

  const daysGrid = [];
  // Leading empty cells
  for (let i = 0; i < startDayOfWeek; i++) {
    daysGrid.push(null);
  }
  // Days of current month
  for (let day = 1; day <= daysInMonth; day++) {
    const currentDayM = viewDate.clone().date(day).startOf("day");
    daysGrid.push(currentDayM);
  }

  // Min/Max disabled check
  const isDayDisabled = (dayM) => {
    if (!dayM) return true;
    if (minDate && moment(minDate).isValid() && dayM.isBefore(moment(minDate).startOf("day"))) {
      return true;
    }
    if (maxDate && moment(maxDate).isValid() && dayM.isAfter(moment(maxDate).endOf("day"))) {
      return true;
    }
    return false;
  };

  const isPrevMonthDisabled = minDate && moment(minDate).isValid() && viewDate.clone().subtract(1, "month").endOf("month").isBefore(moment(minDate).startOf("day"));
  const isNextMonthDisabled = maxDate && moment(maxDate).isValid() && viewDate.clone().add(1, "month").startOf("month").isAfter(moment(maxDate).endOf("day"));

  // Formatted display text
  const displayValue = selectedMoment ? selectedMoment.format(format) : "";

  return (
    <div className={`position-relative w-100 ${className}`} ref={containerRef} id={id}>
      {label && <label className="agenda-drawer-label mb-1 d-block">{label}</label>}

      {/* Input Trigger Box */}
      <div
        className={`mdtp-trigger-box ${error ? "has-error" : ""} ${disabled ? "is-disabled" : ""} ${isOpen ? "is-focused" : ""}`}
        onClick={handleTriggerClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        <span className={`mdtp-trigger-value ${!displayValue ? "is-placeholder" : ""}`}>
          {displayValue || placeholder}
        </span>
        <span className="mdtp-trigger-icon">
          <LuCalendar />
        </span>
      </div>

      {/* Popup Modal with Backdrop Overlay via Portal */}
      {isOpen &&
        createPortal(
          <div className="mdtp-modal-overlay" onClick={handleCancel}>
            <div
              className="mdtp-modal-dialog"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header: Month & Year + Nav Arrows */}
              <div className="mdtp-header">
                <span className="mdtp-header-title">
                  {viewDate.format("MMMM YYYY")}
                </span>
                <div className="mdtp-header-nav">
                  <button
                    type="button"
                    className="mdtp-nav-btn"
                    onClick={handlePrevMonth}
                    disabled={isPrevMonthDisabled}
                    title="Previous month"
                  >
                    <LuChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    className="mdtp-nav-btn"
                    onClick={handleNextMonth}
                    disabled={isNextMonthDisabled}
                    title="Next month"
                  >
                    <LuChevronRight size={16} />
                  </button>
                </div>
              </div>

              {/* Selected Date Summary */}
              <div className="mdtp-summary-strip">
                <div className="mdtp-summary-content">
                  <LuCalendar className="mdtp-summary-icon" size={14} />
                  <span className="mdtp-summary-text">
                    {pendingDate ? pendingDate.format("ddd, MMM D, YYYY") : "Select a date"}
                  </span>
                </div>
              </div>

              {/* Weekday Headers (7 columns) */}
              <div className="mdtp-calendar-weekdays">
                {WEEKDAYS.map((wd) => (
                  <span key={wd} className="mdtp-weekday-label">
                    {wd}
                  </span>
                ))}
              </div>

              {/* Calendar Days Grid */}
              <div className="mdtp-calendar-days-grid">
                {daysGrid.map((dayM, idx) => {
                  if (!dayM) {
                    return <div key={`empty-${idx}`} className="mdtp-day-cell is-empty" />;
                  }

                  const isToday = dayM.isSame(moment(), "day");
                  const isSelected = pendingDate && dayM.isSame(pendingDate, "day");
                  const isDisabled = isDayDisabled(dayM);

                  return (
                    <button
                      key={dayM.format("YYYY-MM-DD")}
                      type="button"
                      className={`mdtp-day-cell ${isToday ? "is-today" : ""} ${isSelected ? "is-selected" : ""} ${isDisabled ? "is-disabled" : ""}`}
                      onClick={() => !isDisabled && handleSelectDay(dayM)}
                      disabled={isDisabled}
                    >
                      {dayM.date()}
                    </button>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="mdtp-footer">
                <button
                  type="button"
                  className="mdtp-btn-cancel"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mdtp-btn-apply"
                  onClick={handleApply}
                  disabled={!pendingDate || isDayDisabled(pendingDate)}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
